import { router } from '@inertiajs/react'
import { useState, useEffect, useRef } from 'react'
import { SendIcon, ArrowLeftIcon, PaperclipIcon, XIcon, ReplyIcon, FileIcon, ImageIcon, FileTextIcon } from 'lucide-react'
import axios from 'axios'
import { toast } from 'sonner'
import authService from '@/lib/auth'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { AppHeader } from '@/components/app-header'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

interface User {
  id: number
  name: string
  email: string
}

interface Attachment {
  id: number
  filename: string
  content_type: string
  byte_size: number
  url: string
}

interface Message {
  id: number
  content: string
  user: User
  created_at: string
  edited_at?: string
  parent_id?: number
  replies_count: number
  read_by: User[]
  attachments: Attachment[]
}

interface Conversation {
  id: number
  type: 'direct_message' | 'channel'
  name: string
  description?: string
  is_private: boolean
  last_message_at?: string
  unread_count: number
  members: User[]
}

interface MessagesShowProps {
  conversation: Conversation
  messages: Message[]
  current_user: User
}

export default function MessagesShow({ conversation, messages, current_user }: MessagesShowProps) {
  const [newMessage, setNewMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Scroll to bottom on mount and when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })

    // Mark conversation as read
    markAsRead()
  }, [messages.length])

  const markAsRead = async () => {
    try {
      await axios.post(`/messages/conversations/${conversation.id}/messages/mark_as_read`, {}, {
        headers: authService.authHeaders()
      })
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return

    setIsSending(true)
    try {
      const formData = new FormData()
      formData.append('content', newMessage || 'Shared files')

      if (replyingTo) {
        formData.append('parent_id', replyingTo.id.toString())
      }

      attachments.forEach((file) => {
        formData.append('attachments[]', file)
      })

      await axios.post(`/messages/conversations/${conversation.id}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...authService.authHeaders()
        }
      })

      setNewMessage('')
      setAttachments([])
      setReplyingTo(null)
      // Reload to get new messages
      router.reload({ only: ['messages'] })
    } catch (error) {
      console.error('Failed to send message:', error)
      toast.error('Failed to send message. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles = files.filter(file => {
      const isValidType = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'application/pdf'].includes(file.type)
      const isValidSize = file.size < 10 * 1024 * 1024 // 10MB

      if (!isValidType) {
        toast.error(`${file.name} is not a supported file type. Only PNG, JPG, GIF, and PDF are allowed.`)
        return false
      }
      if (!isValidSize) {
        toast.error(`${file.name} is too large. Maximum file size is 10MB.`)
        return false
      }
      return true
    })

    setAttachments(prev => [...prev, ...validFiles])
  }

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return ImageIcon
    if (contentType === 'application/pdf') return FileTextIcon
    return FileIcon
  }

  const shouldGroupWithPrevious = (currentMessage: Message, previousMessage: Message | null) => {
    if (!previousMessage) return false

    // Same user
    if (currentMessage.user.id !== previousMessage.user.id) return false

    // Within 2 minutes
    const currentTime = new Date(currentMessage.created_at).getTime()
    const previousTime = new Date(previousMessage.created_at).getTime()
    const timeDiff = (currentTime - previousTime) / 1000 / 60 // minutes

    return timeDiff <= 2
  }

  const handleBack = () => {
    router.visit('/messages/conversations')
  }

  const handleReply = (message: Message) => {
    setReplyingTo(message)
  }

  const cancelReply = () => {
    setReplyingTo(null)
  }

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Messages', url: '/messages/conversations' },
        { label: conversation.name }
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col">
          <div className="flex flex-col h-[calc(100vh-8rem)] px-4 lg:px-6 py-4">
            {/* Header */}
            <div className="mb-4 pb-4 border-b">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="mr-2"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                </Button>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold">
                    {conversation.type === 'channel' && '#'}{conversation.name}
                  </h2>
                  {conversation.description && (
                    <p className="text-sm text-muted-foreground">
                      {conversation.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {conversation.members.length} member{conversation.members.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto mb-4 space-y-4">
              {messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((message, index) => {
                  const isOwnMessage = message.user.id === current_user.id
                  const previousMessage = index > 0 ? messages[index - 1] : null
                  const isGrouped = shouldGroupWithPrevious(message, previousMessage)
                  const parentMessage = message.parent_id ? messages.find(m => m.id === message.parent_id) : null

                  return (
                    <div
                      key={message.id}
                      className={`flex gap-2 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} ${message.parent_id ? 'ml-12' : ''} ${isGrouped ? 'mt-1' : 'mt-4'} group`}
                    >
                      {/* Avatar - show only for first message in group */}
                      {!isOwnMessage && !isGrouped ? (
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs">
                            {getInitials(message.user.name)}
                          </AvatarFallback>
                        </Avatar>
                      ) : !isOwnMessage ? (
                        <div className="w-8" /> // Spacer for grouped messages
                      ) : null}

                      <div className={`flex flex-col max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                        {/* Name and timestamp - show only for first message in group */}
                        {!isGrouped && (
                          <div className="flex items-center gap-2 mb-1 px-3">
                            <span className="text-sm font-medium text-muted-foreground">
                              {message.user.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatTimestamp(message.created_at)}
                            </span>
                          </div>
                        )}

                        {/* Reply indicator */}
                        {parentMessage && (
                          <div className={`mb-2 text-xs text-muted-foreground px-3 py-1 rounded-lg ${isOwnMessage ? 'bg-blue-500/10' : 'bg-muted/50'}`}>
                            <span className="font-medium">{parentMessage.user.name}:</span> {parentMessage.content.substring(0, 100)}{parentMessage.content.length > 100 ? '...' : ''}
                          </div>
                        )}

                        {/* Message bubble */}
                        <div
                          className={`px-4 py-2 rounded-2xl shadow-sm border ${
                            isOwnMessage
                              ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-400/30'
                              : 'bg-gray-100 dark:bg-gray-800 text-foreground border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {message.content}
                          </p>

                          {/* Attachments */}
                          {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-3 space-y-2">
                              {message.attachments.map((attachment) => {
                                const FileIconComponent = getFileIcon(attachment.content_type)
                                const isImage = attachment.content_type.startsWith('image/')

                                return (
                                  <div key={attachment.id}>
                                    {isImage ? (
                                      <a
                                        href={attachment.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block"
                                      >
                                        <img
                                          src={attachment.url}
                                          alt={attachment.filename}
                                          className="max-w-sm max-h-64 rounded-xl"
                                        />
                                      </a>
                                    ) : (
                                      <a
                                        href={attachment.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                                          isOwnMessage
                                            ? 'bg-white/10 hover:bg-white/20'
                                            : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800'
                                        }`}
                                      >
                                        <FileIconComponent className="h-5 w-5 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium truncate">{attachment.filename}</p>
                                          <p className={`text-xs ${isOwnMessage ? 'text-white/70' : 'text-muted-foreground'}`}>
                                            {formatFileSize(attachment.byte_size)}
                                          </p>
                                        </div>
                                      </a>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}

                          {/* Edited indicator */}
                          {message.edited_at && (
                            <p className={`text-xs mt-1 ${isOwnMessage ? 'text-white/70' : 'text-muted-foreground'}`}>
                              (edited)
                            </p>
                          )}
                        </div>

                        {/* Reply button and count - show on hover */}
                        <div className="flex items-center gap-2 mt-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReply(message)}
                            className="h-6 px-2 text-xs"
                          >
                            <ReplyIcon className="h-3 w-3 mr-1" />
                            Reply
                          </Button>
                          {message.replies_count > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {message.replies_count} {message.replies_count === 1 ? 'reply' : 'replies'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="border-t pt-4">
              {replyingTo && (
                <div className="mb-3 flex items-center gap-2 bg-accent px-3 py-2 rounded-md text-sm">
                  <ReplyIcon className="h-4 w-4" />
                  <div className="flex-1">
                    <span className="font-medium">Replying to {replyingTo.user.name}:</span>{' '}
                    <span className="text-muted-foreground">
                      {replyingTo.content.substring(0, 50)}{replyingTo.content.length > 50 ? '...' : ''}
                    </span>
                  </div>
                  <button
                    onClick={cancelReply}
                    className="hover:text-destructive"
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
              {attachments.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-accent px-3 py-2 rounded-md text-sm"
                    >
                      <PaperclipIcon className="h-4 w-4" />
                      <span className="truncate max-w-[200px]">{file.name}</span>
                      <button
                        onClick={() => removeAttachment(index)}
                        className="hover:text-destructive"
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpg,image/jpeg,image/gif,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSending}
                  className="h-[60px] w-[60px]"
                >
                  <PaperclipIcon className="h-5 w-5" />
                </Button>
                <Textarea
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="min-h-[60px] resize-none"
                  disabled={isSending}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && attachments.length === 0) || isSending}
                  size="icon"
                  className="h-[60px] w-[60px]"
                >
                  <SendIcon className="h-5 w-5" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Press Enter to send, Shift+Enter for new line. Click Reply on any message to create a thread.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
